import { Link } from "expo-router";
import { CheckCircle, Download, FileText, Mail, ShieldCheck } from "../../../components/lucide-icons";
import { ReactNode } from "react";
import { Alert, StyleSheet, Text, View } from "react-native";

import { Badge, Button, Panel } from "../../../components/ui";
import { PageShell } from "../../../components/layout";
import { isCentralHost } from "../../../lib/hosting";
import { central, palette, spacing } from "../../../theme/tokens";
import { MonarchDemoShell, NextActionBar } from "../components/arrangement-demo-components";
import { demoArrangementCase } from "../data/demo-arrangement-case";
import { arrangementRoutes } from "../routes";
import { FuneralStatementSection } from "../types";
import { buildMonarchStatementDraft, paperworkCompletionLabel } from "../utils/monarch-paperwork-map";
import { downloadMonarchStatementPdf } from "../utils/statement-pdf";

export default function MonarchStatementPreviewScreen() {
  const centralHost = isCentralHost();
  const draft = buildMonarchStatementDraft(demoArrangementCase);
  const sections = [
    draft.serviceCharges,
    draft.merchandiseCharges,
    draft.specialCharges,
    draft.cashAdvanceItems,
  ];
  const handleDownload = () => {
    const result = downloadMonarchStatementPdf(draft);
    if (!result.ok) {
      Alert.alert("PDF download", result.reason ?? "PDF download is not available here.");
    }
  };

  return (
    <StatementShell>
      <Panel spotlight style={styles.heroPanel}>
        <View style={styles.heroHeader}>
          <View style={styles.documentIcon}>
            <FileText color={palette.gold} size={28} strokeWidth={1.7} />
          </View>
          <View style={styles.heroCopy}>
            <Badge label="Review Required" tone="gold" />
            <Text style={[styles.title, centralHost ? styles.centralTitle : null]}>Statement of Funeral Goods and Services Selected</Text>
            <Text style={styles.subtitle}>
              {draft.lovedOneName} · {draft.dateOfStatementLabel}
            </Text>
          </View>
        </View>

        <View style={styles.statusRow}>
          <StatusBlock centralHost={centralHost} icon={ShieldCheck} title="Draft status" body={paperworkCompletionLabel(draft)} />
          <StatusBlock centralHost={centralHost} icon={Mail} title="Recipient" body={draft.emailRecipients.join(", ")} />
          <StatusBlock centralHost={centralHost} icon={Download} title="Output" body="Downloadable draft PDF" />
          <StatusBlock centralHost={centralHost} icon={CheckCircle} title="Guardrail" body="Review before signature" />
        </View>
      </Panel>

      <Panel style={styles.purchaserPanel}>
        <Text style={styles.panelTitle}>Family and purchaser</Text>
        <View style={styles.detailGrid}>
          <Detail centralHost={centralHost} label="Loved one" value={draft.lovedOneName} />
          <Detail centralHost={centralHost} label="Date of passing" value={draft.dateOfPassingLabel} />
          <Detail centralHost={centralHost} label="Purchaser" value={draft.purchaserName} />
          <Detail centralHost={centralHost} label="Phone" value={draft.purchaserPhone} />
        </View>
      </Panel>

      <View style={styles.sectionStack}>
        {sections.map((section) => (
          <StatementSection key={section.id} section={section} />
        ))}
      </View>

      <Panel style={styles.reviewPanel}>
        <View style={styles.inlineHeader}>
          <ShieldCheck color={palette.forest} size={20} strokeWidth={1.8} />
          <Text style={styles.panelTitle}>Before this is sent</Text>
        </View>
        <View style={styles.missingGrid}>
          {draft.missingFields.map((field) => (
            <View key={field} style={[styles.missingPill, centralHost ? styles.centralMissingPill : null]}>
              <Text style={styles.missingText}>{field}</Text>
            </View>
          ))}
        </View>
        <Text style={styles.disclosureText}>{draft.disclosures.join(" ")}</Text>
        <NextActionBar>
          <Button label="Download Draft PDF" onPress={handleDownload} />
          <Button label="Email Draft" />
          <Link href={arrangementRoutes.directorCase(demoArrangementCase.id) as never} asChild>
            <Button label="Back to Case" variant="secondary" />
          </Link>
        </NextActionBar>
      </Panel>
    </StatementShell>
  );
}

function StatementShell({ children }: { children: ReactNode }) {
  if (isCentralHost()) {
    return <PageShell role="staff"><View style={styles.centralContent}>{children}</View></PageShell>;
  }
  return <MonarchDemoShell activeLabel="Statement">{children}</MonarchDemoShell>;
}

function StatementSection({ section }: { section: FuneralStatementSection }) {
  return (
    <Panel style={styles.statementSection}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>{section.title}</Text>
        <Text style={styles.sectionTotal}>{section.totalLabel}</Text>
      </View>
      <View style={styles.lineStack}>
        {section.lineItems.map((item) => (
          <View key={item.id} style={styles.lineItem}>
            <View style={styles.lineCopy}>
              <Text style={styles.lineLabel}>{item.label}</Text>
              <Text style={styles.lineSource}>{item.needsReview ? "Needs review" : "No family selection"}</Text>
            </View>
            <Text style={styles.lineAmount}>{item.amountLabel}</Text>
          </View>
        ))}
      </View>
    </Panel>
  );
}

function StatusBlock({
  centralHost,
  icon: Icon,
  title,
  body,
}: {
  centralHost: boolean;
  icon: (props: { color: string; size: number; strokeWidth?: number }) => ReactNode;
  title: string;
  body: string;
}) {
  return (
    <View style={[styles.statusBlock, centralHost ? styles.centralInset : null]}>
      <Icon color={palette.cedar} size={18} strokeWidth={1.8} />
      <View style={styles.statusCopy}>
        <Text style={styles.statusTitle}>{title}</Text>
        <Text style={styles.statusBody}>{body}</Text>
      </View>
    </View>
  );
}

function Detail({ centralHost, label, value }: { centralHost: boolean; label: string; value: string }) {
  return (
    <View style={[styles.detail, centralHost ? styles.centralInset : null]}>
      <Text style={styles.detailLabel}>{label}</Text>
      <Text style={styles.detailValue}>{value}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  centralContent: {
    gap: spacing.md,
  },
  heroPanel: {
    gap: spacing.lg,
  },
  heroHeader: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.md,
    alignItems: "center",
  },
  documentIcon: {
    width: 58,
    height: 58,
    borderRadius: 16,
    borderCurve: "continuous",
    backgroundColor: "rgba(182, 134, 45, 0.12)",
    alignItems: "center",
    justifyContent: "center",
  },
  heroCopy: {
    flex: 1,
    minWidth: 250,
    gap: 8,
  },
  title: {
    color: palette.ink,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 40,
    lineHeight: 42,
  },
  centralTitle: {
    color: central.text.primary,
    fontFamily: "Manrope_700Bold",
    fontSize: 24,
    lineHeight: 31,
  },
  centralInset: {
    backgroundColor: central.surface.muted,
    borderColor: central.border.subtle,
    borderRadius: central.radius.control,
  },
  subtitle: {
    color: palette.inkSoft,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 14,
    lineHeight: 20,
  },
  statusRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.sm,
  },
  statusBlock: {
    flex: 1,
    minWidth: 240,
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
    padding: 14,
    borderRadius: 14,
    borderCurve: "continuous",
    backgroundColor: "rgba(251, 248, 241, 0.72)",
    borderWidth: 1,
    borderColor: "rgba(150, 133, 97, 0.28)",
  },
  statusCopy: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  statusTitle: {
    color: palette.ink,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 13,
    lineHeight: 18,
  },
  statusBody: {
    color: palette.inkSoft,
    fontFamily: "Manrope_500Medium",
    fontSize: 12,
    lineHeight: 18,
    flexShrink: 1,
  },
  purchaserPanel: {
    gap: spacing.md,
  },
  panelTitle: {
    color: palette.ink,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 16,
    lineHeight: 22,
  },
  detailGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
  },
  detail: {
    minWidth: 220,
    flexBasis: "48%",
    flexGrow: 1,
    gap: 3,
    padding: 12,
    borderRadius: 14,
    borderCurve: "continuous",
    backgroundColor: "rgba(234, 223, 201, 0.34)",
  },
  detailLabel: {
    color: palette.inkSoft,
    fontFamily: "Manrope_700Bold",
    fontSize: 11,
    lineHeight: 15,
    textTransform: "uppercase",
  },
  detailValue: {
    color: palette.ink,
    fontFamily: "Manrope_700Bold",
    fontSize: 14,
    lineHeight: 20,
    flexShrink: 1,
  },
  sectionStack: {
    gap: spacing.md,
  },
  statementSection: {
    gap: spacing.md,
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 10,
  },
  sectionTitle: {
    color: palette.forest,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 15,
    lineHeight: 21,
  },
  sectionTotal: {
    color: palette.cedar,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 13,
    lineHeight: 19,
  },
  lineStack: {
    gap: 9,
  },
  lineItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 12,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: "rgba(150, 133, 97, 0.18)",
  },
  lineCopy: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  lineLabel: {
    color: palette.ink,
    fontFamily: "Manrope_700Bold",
    fontSize: 13,
    lineHeight: 18,
  },
  lineSource: {
    color: palette.inkSoft,
    fontFamily: "Manrope_500Medium",
    fontSize: 11,
    lineHeight: 15,
  },
  lineAmount: {
    color: palette.inkSoft,
    fontFamily: "Manrope_700Bold",
    fontSize: 12,
    lineHeight: 18,
    textAlign: "right",
    maxWidth: 150,
  },
  reviewPanel: {
    gap: spacing.md,
  },
  inlineHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 9,
  },
  missingGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  missingPill: {
    borderRadius: 999,
    backgroundColor: "rgba(182, 134, 45, 0.12)",
    borderWidth: 1,
    borderColor: "rgba(182, 134, 45, 0.26)",
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  centralMissingPill: {
    borderRadius: central.radius.badge,
  },
  missingText: {
    color: palette.cedar,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 12,
    lineHeight: 16,
  },
  disclosureText: {
    color: palette.inkSoft,
    fontFamily: "Manrope_500Medium",
    fontSize: 13,
    lineHeight: 20,
  },
});
